import { Box, ButtonBase, FormHelperText, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DEFAULT_THEME_OPTION, findThemeOption, THEME_OPTIONS, ThemeOption } from './parameters-utils';

interface ThemePickerProps {
    value: string;
    onChange: (value: string) => void;
}

// Miniatura do portal com as cores da paleta: barra superior, destaque e cartões
function ThemeMockup({ option }: { option: ThemeOption }) {
    return (
        <Box sx={{ width: '100%', borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: '#f5f5f5' }}>
            <Box sx={{ height: 14, bgcolor: option.primary, display: 'flex', alignItems: 'center', px: 0.75, gap: 0.5 }}>
                <Box sx={{ width: 18, height: 4, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.85)' }} />
                <Box sx={{ flex: 1 }} />
                <Box sx={{ width: 8, height: 4, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.6)' }} />
                <Box sx={{ width: 8, height: 4, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.6)' }} />
            </Box>
            <Box sx={{ height: 22, bgcolor: option.secondary }} />
            <Box sx={{ display: 'flex', gap: 0.5, p: 0.75 }}>
                {[0, 1, 2].map((i) => (
                    <Box key={i} sx={{ flex: 1, height: 18, bgcolor: '#fff', borderRadius: 0.5, borderTop: '3px solid', borderTopColor: option.accent }} />
                ))}
            </Box>
        </Box>
    );
}

export function ThemePicker({ value, onChange }: ThemePickerProps) {
    const selected = findThemeOption(value);

    return (
        <Box>
            <Box
                role="radiogroup"
                aria-label="Tema de cores do portal"
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}
            >
                {THEME_OPTIONS.map((option) => {
                    const isSelected = selected?.value === option.value;
                    return (
                        <ButtonBase
                            key={option.value}
                            role="radio"
                            aria-checked={isSelected}
                            // Mantém o número gravado quando ele já pertence à paleta (ex.: tema 4 continua 4)
                            onClick={() => !isSelected && onChange(String(option.value))}
                            sx={{
                                position: 'relative',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: 1,
                                p: 1.25,
                                borderRadius: 1.5,
                                border: '2px solid',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                                textAlign: 'left',
                                transition: 'border-color 0.15s',
                                '&:hover': { borderColor: isSelected ? 'primary.main' : 'text.secondary' },
                                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                            }}
                        >
                            <ThemeMockup option={option} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: option.primary, flexShrink: 0 }} />
                                <Typography variant="body2" fontWeight={isSelected ? 600 : 500} noWrap>{option.name}</Typography>
                            </Box>
                            {isSelected && (
                                <CheckCircleIcon color="primary" sx={{ position: 'absolute', top: 4, right: 4, fontSize: 20, bgcolor: 'background.paper', borderRadius: '50%' }} />
                            )}
                        </ButtonBase>
                    );
                })}
            </Box>
            {!selected && (
                <FormHelperText>Nenhum tema definido: o portal usa o {DEFAULT_THEME_OPTION.name}.</FormHelperText>
            )}
        </Box>
    );
}
